import React from 'react';
import { useAgilityContent } from 'hooks/useAgilityContent';
import ContactUsBanner from './ContactUsBanner';

/**
 * Example: Wrapper component that fetches Agility data and passes it to ContactUsBanner
 * This demonstrates the second approach of embedding a component that fetches its own data
 *
 * Usage in ContactUsHero or any other component:
 * <ContactUsBannerWithAgilityData agilityContentListName="your-list-name" />
 */
interface ContactUsBannerWithAgilityDataProps {
  agilityContentListName?: string;
  locale?: string;
  // Any other props to pass to ContactUsBanner
  [key: string]: any;
}

const ContactUsBannerWithAgilityData: React.FC<
  ContactUsBannerWithAgilityDataProps
> = ({ agilityContentListName, locale = 'en-us', ...rest }) => {
  // Fetch Agility content using the custom hook
  const {
    data: agilityContent,
    isLoading,
    error,
  } = useAgilityContent(agilityContentListName, locale);

  // Show loading state
  if (isLoading) {
    return <div className="p-4 text-center">Loading contact form data...</div>;
  }

  // Show error state with debugging info
  if (error) {
    console.error('Failed to load Agility content:', error);
    return (
      <div className="p-4 text-center text-red-600 bg-red-50 rounded border border-red-200">
        <p className="font-semibold mb-2">Failed to load form data</p>
        <p className="text-sm">
          {error instanceof Error ? error.message : String(error)}
        </p>
        <p className="text-xs mt-2 text-gray-600">
          Content List: <code>{agilityContentListName}</code>
        </p>
      </div>
    );
  }

  // Render the ContactUsBanner with fetched data, providing empty fields if content is missing
  // Hide the marketing callout column since the parent hero already renders its own heading
  const fields = agilityContent || {};
  return <ContactUsBanner hideCallout {...rest} module={{ fields }} />;
};

export default ContactUsBannerWithAgilityData;
